import { Suspense, lazy, useEffect, useRef } from 'react';
import {
  animateScrollToBottom, prefersReducedMotion, scrollParentOf, SCROLL_TO_BOTTOM_MS,
} from '../../lib/smooth-scroll';
import { AlertCircle, Sparkles, Paperclip } from '../../icons';
import { useAiChatStore } from '../../store/aiChatStore';

/**
 * ★★ **늦게 불러온다.** 이 패널은 `AppLayout` 이 eager 로 들여오므로, 마크다운 렌더러를
 *   그냥 import 하면 `react-markdown`·`remark-gfm` 이 **셸 번들에 실려 모든 페이지**가
 *   그 무게를 진다 — 정작 쓰는 건 패널을 연 사람뿐이다.
 */
const AiAnswerBody = lazy(() => import('./AiAnswerBody'));
import { AiConfirmCard } from './AiConfirmCard';

/**
 * 대화 목록 — 패널과 AI 메뉴가 **같은 것**을 쓴다 (설계 판 B · 정의서 M6).
 *
 * ★ 두 벌로 만들면 한쪽만 고쳐지는 날이 온다. 폭만 다르고(400 ↔ 760) 부품은 하나다.
 * ★ 대화는 `aiChatStore` 가 들고 있다 — 패널을 닫아도, 패널에서 AI 메뉴로 넘어가도 이어진다.
 *
 * ## ★★★ 마크다운으로 그린다 (2026-09-11) — 미뤄 둔 판단이 «사실»에 밀렸다
 *
 * 여기엔 「아직 마크다운으로 그리지 않는다 — 이 판의 답은 대개 짧은 글이고, 렌더러를 붙이면
 * 남이 쓴 글이 실행되는 자리가 생긴다」고 적혀 있었다. **두 전제가 다 바뀌었다:**
 *
 *   1. **답이 짧지 않다.** 서버의 합성 포맷 기본값이 `markdown` 이고, 「마크다운 표로
 *      정리하세요」·「콜아웃(`> [!WARNING]`)으로 강조하세요」를 **직접 지시**한다.
 *      그래서 화면에는 `**굵게**` 의 별 두 개가 **글자 그대로** 보이고 있었다(사용자 지적).
 *   2. **위험은 이미 같은 렌더러로 다뤄지고 있다.** 채팅의 AI 답(`AiTextMessage`·
 *      `AiStreamingMessage`)이 **같은 orchestrator 출력**을 이 렌더러로 그린다 —
 *      링크는 http/https/mailto 만(그 밖은 글자로), html 펜스는 DOMPurify, mermaid 는 strict.
 *      AI 패널만 안 쓰는 것은 «더 안전»한 것이 아니라 **혼자 낡은 것**이었다.
 *
 * ⭑ 이런 「아직 안 한다」 주석을 만나면 **그 전제가 아직 참인지부터** 확인한다.
 * ★ 사람이 쓴 말(`aiconv__bubble`)과 실패 안내는 **그대로 글자**다 — 내가 친 글이
 *   제멋대로 굵어지면 그게 더 이상하다.
 */
export function AiConversation({ emptyHint }: { emptyHint?: React.ReactNode }) {
  const messages = useAiChatStore((s) => s.messages);
  const waiting = useAiChatStore((s) => s.waiting);
  const resolveConfirm = useAiChatStore((s) => s.resolveConfirm);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  /** 스트리밍 중인 것은 **마지막 말 하나**뿐이다 — 그것만 「오는 중」으로 그린다. */
  const lastId = messages.length > 0 ? messages[messages.length - 1]!.id : null;

  /**
   * 새 말이 붙거나 글자가 늘면 바닥으로 따라간다 — 스트리밍 중에도 마지막 줄이 보여야 한다.
   *
   * ★★ **두 가지를 가른다**(2026-09-11 사용자 요청 「살짝 천천히 내려가도록」):
   *   · **말이 새로 붙었을 때**(엔터를 쳤을 때·답이 시작될 때) → 450ms 로 **부드럽게** 내려간다
   *   · **글자가 자라는 동안**(스트리밍) → 종전처럼 **곧장** 따라간다
   *
   *   ⭑ 스트리밍에도 애니메이션을 걸면 글자가 올 때마다 **처음부터 다시 시작**해 덜컹거린다.
   *     「내려가는 순간」은 사람이 무언가를 한 그 한 번뿐이다.
   * ★ 「움직임을 줄여 달라」고 해 둔 사람에게는 애니메이션 없이 곧장 간다.
   */
  const prevCount = useRef(0);
  const stopRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const sentinel = bottomRef.current;
    if (!sentinel) return;
    const added = messages.length > prevCount.current;
    prevCount.current = messages.length;

    if (added) {
      stopRef.current?.();                 // 앞엣것을 멈추고 새로 시작한다
      const box = prefersReducedMotion() ? null : scrollParentOf(sentinel);
      if (box) {
        stopRef.current = animateScrollToBottom(box, SCROLL_TO_BOTTOM_MS, () => {
          stopRef.current = null;
        });
        return;
      }
    } else if (stopRef.current) {
      /*
       * ⚠⚠⚠ **여기서 곧장 뛰면 애니메이션이 «0.1초»만에 죽는다** (2026-09-11 사용자 지적).
       *
       * 엔터를 치면 말이 둘 붙고(내 말 + 빈 답) 애니메이션이 시작되는데, **곧바로 스트리밍
       * 첫 글자가 도착**해 이 효과가 다시 돈다. 그때는 `added` 가 거짓이라 아래 «곧장» 가지로
       * 떨어져, 내려가던 것을 지우고 순간이동시켰다 — 사용자가 본 「0.1초만에 툭」이 그것이다.
       *
       * ⭑ **내려가는 중이면 그대로 둔다.** 애니메이션은 매 프레임 바닥을 다시 읽으므로
       *   글자가 자라는 것도 알아서 따라간다(그러라고 그렇게 만들었다).
       */
      return;
    }

    /* 구르는 상자를 못 찾거나(레이아웃 전) 글자만 자란 경우 — 종전 그대로 곧장. */
    sentinel.scrollIntoView({ block: 'end' });
  }, [messages, waiting]);

  /* ★ 멈추는 것은 **떠날 때뿐**이다 — 다시 그릴 때마다 멈추면 위의 사고가 되돌아온다. */
  useEffect(() => () => stopRef.current?.(), []);

  if (messages.length === 0) {
    return (
      <div className="aiconv aiconv--empty">
        <div className="aiconv__hello">
          <span className="aiconv__mark"><Sparkles size={20} aria-hidden /></span>
          <h3>무엇을 도와드릴까요</h3>
          {emptyHint}
        </div>
      </div>
    );
  }

  return (
    <div className="aiconv" role="log" aria-label="AI 대화" aria-live="polite">
      {messages.map((m) => (
        <div key={m.id} className={`aiconv__m${m.role === 'user' ? ' aiconv__m--me' : ''}`}>
          {m.role === 'user' ? (
            <div className="aiconv__bubble">
              {m.text}
              {/* ★ 무엇을 붙여 물었는지 남긴다 (2026-09-10) — 안 남기면 대화를 다시 읽을 때
                  답이 왜 그렇게 나왔는지 알 수 없다. 내용은 서버가 읽었고 여기엔 이름만 남는다. */}
              {m.files && m.files.length > 0 && (
                <span className="aiconv__files">
                  {m.files.map((n) => (
                    <span key={n} className="aiconv__file">
                      <Paperclip size={11} aria-hidden />
                      {n}
                    </span>
                  ))}
                </span>
              )}
            </div>
          ) : m.failed ? (
            /* 실패는 «답»이 아니라 «안내»다 — 답변 카드와 다른 모양으로 그려야
               다음에 대화를 읽을 때 이것이 AI 가 한 말이 아님을 안다. */
            <p className="aiconv__fail">
              <AlertCircle size={14} aria-hidden />
              {m.text}
            </p>
          ) : (
            <div className="aiconv__answer">
              {/* ★ 아직 오는 중인 마지막 답은 «열린 코드펜스»를 가려서 그린다 —
                     반쯤 온 표가 덜컹거리며 자라지 않게(렌더러가 그 일을 한다). */}
              {/* 대체 화면은 «글자 그대로» — 예전과 같은 모습이라 바뀌는 순간이 안 보인다. */}
              <Suspense fallback={m.text}>
                <AiAnswerBody text={m.text} streaming={waiting && m.id === lastId} />
              </Suspense>
              {/* 마지막 답이 아직 오는 중이면 커서를 붙인다. */}
              {!m.text && waiting && !m.confirm && <span className="aiconv__thinking">생각하고 있습니다…</span>}
              {/* ★★★ 쓰기 도구가 실행 «전에» 멈추고 묻는다 — 답 아래에 카드를 단다.
                     승인하면 **같은 말풍선**에 답이 이어 붙는다(한 질문 한 답). */}
              {m.confirm && (
                <AiConfirmCard
                  confirm={m.confirm}
                  onDecide={(d) => { resolveConfirm(m.id, d); }}
                />
              )}
            </div>
          )}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
